import React, { useState, useEffect, createRef, createContext, ReactNode } from "react";
import { useSelector } from "react-redux";
import { DatePicker, DatePickerProps, Input, message, Select, Switch, Tooltip } from "antd";
import validator from "@rjsf/validator-ajv6";
import { withTheme } from "@rjsf/core";
import { Theme } from "@rjsf/antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import type { ErrorTransformer, UiSchema } from "@rjsf/utils";
import { Loading } from "../../components/loading";
import { Version } from "../../types/version";
import $authHost from "../../http/auth";
import styles from "./tenants.module.css";
import { getUser } from "../selectors";
import permissions from "../../constants/permissions";
import { replaceUndefinedWithEmptyString } from "../../utils/replaceUndefinedWithEmptyString";
import jsonMock from "./applicationFromMock.json";
import { AccordionField } from "../../components/rjsf/accordionField";
import { AccordionFieldArray } from "../../components/rjsf/accordionFieldArray";
import { getRefName } from "../../utils/getRefName";
import { SearchContext } from "../../constants/contexts";
import { getSchemaAtPath } from "../../utils/getSchemaAtPath";
import { ConfirmingArrayFieldItemTemplate } from "../../components/rjsf/confirmingArrayFieldItemTemplate";

const { Option } = Select;

// @ts-ignore
const Form = withTheme(Theme);

export const ApplicationForm = ({ appName, applicationId, onApplicationSave, tenantId, useMocks = false }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);
  const [enabledTill, setEnabledTill] = useState<string>("");
  const [version, setVersion] = useState<Version>();
  const [uiSchema, setUiSchema] = useState<Object>({});
  const [versions, setVersions] = useState<Version[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<null | string>(null);
  const formRef = createRef<any>();
  const user = useSelector(getUser);
  const { t } = useTranslation("tenants");

  const userCanReadConfiguration = user.permissions.includes(permissions.MANAGE_CONFIGURATIONS_READ);
  const userCanWriteConfiguration = user.permissions.includes(permissions.MANAGE_CONFIGURATIONS_WRITE);

  useEffect(() => {
    if (applicationId == null || tenantId == null) {
      return;
    }
    Promise.all([
      $authHost.get(`/Configurations/${user.role}/application/${applicationId}/versions`),
      $authHost.get(`/Configurations/${user.role}/application/${applicationId}/tenant/${tenantId}`),
    ])
      .then(([versionsRes, applicationRes]) => {
        console.log("versions", versionsRes.data);
        console.log("application", applicationRes.data);
        setVersions(versionsRes.data);
        if (useMocks) {
          setUiSchema(generateUiSchema(jsonMock));
          setVersion({ ...applicationRes.data, schema: jsonMock });
        } else {
          setUiSchema(generateUiSchema(applicationRes.data.schema));
          setVersion(applicationRes.data);
        }
        setIsEnabled(applicationRes.data.isEnabled);
        setEnabledTill(applicationRes.data.enabledTill);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  }, [applicationId, tenantId, user]);

  const generateUiSchema = (schema: UiSchema, searchTerm = "") => {
    const uiSchema: UiSchema = {};

    if (schema?.properties) {
      for (let key in schema.properties) {
        let type = schema.properties[key].type;
        if (!type && schema.properties[key].$ref && schema?.definitions) {
          const ref = getRefName(schema.properties[key].$ref);
          type = schema.definitions[ref]?.type;
        }

        switch (type) {
          case "array": {
            const result: UiSchema = {};
            result["ui:FieldTemplate"] = AccordionFieldArray;

            uiSchema[key] = {
              ...result,
              ...generateUiSchema(schema.properties[key], searchTerm),
            };
            break;
          }
          case "object": {
            const result: UiSchema = {};
            result["ui:ObjectFieldTemplate"] = AccordionField;

            uiSchema[key] = {
              ...result,
              ...generateUiSchema(schema.properties[key], searchTerm),
            };
            break;
          }
          default: {
            const result: UiSchema = {};
            if (!key.toLowerCase().startsWith(searchTerm.toLowerCase())) {
              result["ui:classNames"] = styles.hiddenField;
            }
            uiSchema[key] = result;
            break;
          }
        }
      }
    }

    return {
      ...uiSchema,
      "ui:options": {
        expandable: false,
      },
    };
  };

  useEffect(() => {
    if (!versions.length || selectedVersionId === null) {
      return;
    }

    const version = versions.find((version) => version.versionId === selectedVersionId);

    if (version) {
      setVersion(version);
    }
  }, [selectedVersionId, versions]);

  const onSubmit = (configuration: any) => {
    setIsSubmitting(true);
    if (
      configuration &&
      configuration.featureToggles &&
      configuration.featureToggles?.sednaIntegrationURL === undefined
    ) {
      configuration.featureToggles.sednaIntegrationURL = "";
    }
    $authHost
      .put(`/Tenants/${user.role}/${tenantId}/applications/${applicationId}`, {
        configuration: replaceUndefinedWithEmptyString(configuration),
        schema: version!.schema,
        versionId: selectedVersionId ?? version!.versionId,
        isEnabled,
        enabledTill: enabledTill || null,
      })
      .then((res) => {
        onApplicationSave(res.data);
        message.success(t("applicationSuccessfullySaved"), 5);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onChangeEnabledTill = (value: DatePickerProps["value"]) => {
    setEnabledTill(value?.toISOString() || "");
  };

  const handleChange = ({ formData }: any) => {
    console.log("formData change", formData);
  };

  const transformErrors: ErrorTransformer = (errors) => {
    return errors.filter((error) => {
      if (error.name === "type" && error.property && error.schemaPath && version) {
        const value = _.get(formRef.current.state.formData, error.property.substring(1));
        const propName = error.property.split(".").pop();

        // Use new robust function to get the right field schema
        const fieldSchema = getSchemaAtPath(version.schema, error.property);

        if (
          propName &&
          fieldSchema &&
          (!fieldSchema.required || !fieldSchema.required.includes(propName)) &&
          value == null
        ) {
          // Field is not required and value is missing/null, so filter out the error
          return false;
        }
      }

      return true;
    });
  };

  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.trimStart();
    setSearchTerm(searchValue);
    const filteredUiSchema = generateUiSchema(version?.schema as UiSchema, searchValue);
    setUiSchema(filteredUiSchema);
  };

  const scrollToTop = () => {
    const formBody = document.getElementsByClassName("ant-drawer-body")[1];
    formBody.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <>
      <div className={styles.applicationFormHeader}>
        <p className={styles.font16}>
          {t("application")}: <strong>{appName}</strong>
        </p>
        <Select
          placeholder={t("selectVersion")}
          value={selectedVersionId}
          onChange={(value) => setSelectedVersionId(value)}
        >
          {versions.map((version) => (
            <Option key={version.versionId} value={version.versionId}>
              {version.number}
            </Option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <>
          {isSubmitting && <Loading />}
          {version && (
            <>
              <div className={styles.applicationFormItem}>
                <p className={styles.applicationFormLabel}>{t("enabled")}</p>
                <Tooltip title={t(isEnabled ? "disable" : "enable")}>
                  <Switch
                    defaultChecked={isEnabled ?? undefined}
                    onClick={(val: boolean) => {
                      setIsEnabled(val);
                    }}
                  />
                </Tooltip>
              </div>

              <div className={styles.applicationFormItem}>
                <label htmlFor="enabled-till" className={styles.applicationFormLabel}>
                  {t("enabledTill")}
                </label>
                <DatePicker value={enabledTill ? moment(enabledTill) : null} onChange={onChangeEnabledTill} />
              </div>
              <div style={{ marginBottom: "6px" }}>
                <Input
                  allowClear
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              {userCanReadConfiguration && (
                <SearchContext.Provider value={{ term: searchTerm }}>
                  <Form
                    className="json-form"
                    schema={{ ...version.schema, $id: "http://json-schema.org/draft-04/schema#" }}
                    formData={version.configuration}
                    transformErrors={transformErrors}
                    ref={formRef}
                    readonly={!userCanWriteConfiguration}
                    uiSchema={uiSchema}
                    templates={{
                      ArrayFieldItemTemplate: ConfirmingArrayFieldItemTemplate,
                    }}
                    validator={validator}
                    onChange={handleChange}
                    onSubmit={(value: any) => onSubmit(value.formData)}
                    onError={(value: any) => console.log("errors", value)}
                  >
                    <div className={styles.submitButtonContainer}>
                      <button type="submit" className={styles.submitButton}>
                        Submit
                      </button>
                    </div>
                  </Form>
                </SearchContext.Provider>
              )}
              <button className={styles.scrollToTop} onClick={scrollToTop}>
                ↑
              </button>
            </>
          )}
        </>
      )}
    </>
  );
};
